/**
 * ---------------------------------------------------------
 * Stripe core primitives
 * ---------------------------------------------------------
 */

import z from "zod";

const UnixTimestamp = z.number().int().nonnegative();

const StripeObjectBase = z.object({
	id: z.string(),
	object: z.string(),
});

/**
 * ---------------------------------------------------------
 * Stripe Event (snapshot / v1)
 * https://docs.stripe.com/api/events/object
 * ---------------------------------------------------------
 */

export const StripeEventSchema = z.object({
	id: z.string(), // evt_*
	object: z.literal("event"),
	api_version: z.string().optional(),
	created: UnixTimestamp,
	livemode: z.boolean(),
	pending_webhooks: z.number().int(),
	request: z
		.object({
			id: z.string().nullable(),
			idempotency_key: z.string().nullable(),
		})
		.nullable(),

	type: z.string(), // e.g. "payment_intent.succeeded"

	data: z.object({
		object: z.unknown(), // refine by event.type
		previous_attributes: z.record(z.string(), z.unknown()).optional(),
	}),
});

/**
 * ---------------------------------------------------------
 * Common Stripe objects (minimal but correct)
 * ---------------------------------------------------------
 */

export const PaymentIntentSchema = StripeObjectBase.extend({
	object: z.literal("payment_intent"),
	amount: z.number(),
	amount_received: z.number(),
	currency: z.string(),
	status: z.enum([
		"requires_payment_method",
		"requires_confirmation",
		"requires_action",
		"processing",
		"requires_capture",
		"canceled",
		"succeeded",
	]),
	customer: z.string().nullable(),
	metadata: z.record(z.string(), z.string()),
});

export const CheckoutSessionSchema = StripeObjectBase.extend({
	object: z.literal("checkout.session"),
	mode: z.enum(["payment", "subscription", "setup"]),
	status: z.enum(["open", "complete", "expired"]).nullable(),
	payment_status: z.enum(["paid", "unpaid", "no_payment_required"]),
	customer: z.string().nullable(),
	subscription: z.string().nullable(),
	payment_intent: z.string().nullable(),
	metadata: z.record(z.string(), z.string()),
});

export const InvoiceSchema = StripeObjectBase.extend({
	object: z.literal("invoice"),
	number: z.string().nullable(),
	status: z.enum(["draft", "open", "paid", "uncollectible", "void"]),
	total: z.number(),
	currency: z.string(),
	customer: z.string().nullable(),
	subscription: z.string().nullable(),
	metadata: z.record(z.string(), z.string()),
});

/**
 * ---------------------------------------------------------
 * Typed event helpers (safe narrowing)
 * ---------------------------------------------------------
 */

export const PaymentIntentSucceededEventSchema = StripeEventSchema.extend({
	type: z.literal("payment_intent.succeeded"),
	data: z.object({
		object: PaymentIntentSchema,
	}),
});

export const CheckoutSessionCompletedEventSchema = StripeEventSchema.extend({
	type: z.literal("checkout.session.completed"),
	data: z.object({
		object: CheckoutSessionSchema,
	}),
});

export const InvoicePaidEventSchema = StripeEventSchema.extend({
	type: z.literal("invoice.paid"),
	data: z.object({
		object: InvoiceSchema,
	}),
});

/**
 * ---------------------------------------------------------
 * Stripe Transfer object (minimal, correct)
 * ---------------------------------------------------------
 */

export const StripeTransferSchema = StripeObjectBase.extend({
	object: z.literal("transfer"),
	amount: z.number(),
	currency: z.string(),
	destination: z.string(),
	created: UnixTimestamp,
	metadata: z.record(z.string(), z.string()).optional(),
});

/**
 * ---------------------------------------------------------
 * Transfer events (used for payout reconciliation)
 * ---------------------------------------------------------
 */

export const StripeTransferCreatedEventSchema = StripeEventSchema.extend({
	type: z.literal("transfer.created"),
	data: z.object({
		object: StripeTransferSchema,
	}),
});

export const StripeTransferReversedEventSchema = StripeEventSchema.extend({
	type: z.literal("transfer.reversed"),
	data: z.object({
		object: StripeTransferSchema.extend({
			failure_code: z.string().nullable().optional(),
			failure_message: z.string().nullable().optional(),
		}),
	}),
});

/**
 * ---------------------------------------------------------
 * Union of known events (extend as you add handlers)
 * ---------------------------------------------------------
 */

export const KnownStripeEventSchema = z.union([
	PaymentIntentSucceededEventSchema,
	CheckoutSessionCompletedEventSchema,
	InvoicePaidEventSchema,
	StripeTransferCreatedEventSchema,
	StripeTransferReversedEventSchema,
]);

/**
 * ---------------------------------------------------------
 * Types
 * ---------------------------------------------------------
 */

export type StripeEvent = z.infer<typeof StripeEventSchema>;
export type KnownStripeEvent = z.infer<typeof KnownStripeEventSchema>;
export type PaymentIntent = z.infer<typeof PaymentIntentSchema>;
export type CheckoutSession = z.infer<typeof CheckoutSessionSchema>;
export type Invoice = z.infer<typeof InvoiceSchema>;

/**
 * ---------------------------------------------------------
 * Usage example (recommended pattern)
 * ---------------------------------------------------------
 *
 * const event = StripeEventSchema.parse(rawEvent);
 *
 * switch (event.type) {
 *   case "payment_intent.succeeded": {
 *     const typed = PaymentIntentSucceededEventSchema.parse(event);
 *     typed.data.object.amount;
 *     break;
 *   }
 *   case "checkout.session.completed": {
 *     const typed = CheckoutSessionCompletedEventSchema.parse(event);
 *     typed.data.object.customer;
 *     break;
 *   }
 * }
 */
